import { Amplify } from "aws-amplify";
import {
  confirmSignIn,
  fetchAuthSession,
  getCurrentUser,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  type SignInOutput,
} from "aws-amplify/auth";
import type { InspectorConfig } from "../api";

// Sign-in against the inspector's Cognito user pool.
//
// The pool has SELF SIGN-UP DISABLED — an administrator creates accounts — so
// this module offers exactly three things: sign in, answer whatever challenge
// the pool sets, sign out. There is no register, no forgot-password, no
// federation.
//
// Authentication happens against the pool directly (SRP), not through Cognito's
// hosted UI, so the sign-in screen is part of this application and looks like it.
// The id token it produces is what the API Gateway JWT authorizer verifies on
// every request; nothing in the browser decides what a user may see.

/** Where a sign-in has got to. Mirrors the challenges the pool can set. */
export type SignInStep =
  | { kind: "credentials" }
  | { kind: "new_password" }
  | { kind: "totp_setup"; secret: string; uri: string }
  | { kind: "totp_code" }
  | { kind: "done" };

let configured = "";

/** Configures Amplify from the config the API served. Safe to call repeatedly. */
export function configureAuth(config: InspectorConfig): void {
  const key = `${config.userPoolId}:${config.clientId}`;
  if (configured === key) return;
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: config.userPoolId,
        userPoolClientId: config.clientId,
      },
    },
  });
  configured = key;
}

/** The id token for the current session, or undefined when signed out. */
export async function currentToken(): Promise<string | undefined> {
  try {
    // forceRefresh: false — Amplify refreshes on its own when the token is near
    // expiry, using the refresh token it holds.
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString();
  } catch {
    return undefined;
  }
}

// Renewing ahead of time. The id token lives an hour; Amplify renews it from
// the refresh token only once it is within seconds of expiry, so a page that
// holds a token — the Deliveries board, left open on a monitor for days —
// would send an expired one for a while. Renewed this far ahead, it never
// does. The refresh token itself lives twelve hours (infra/lib/inspector.ts):
// after that, nothing renews, and the user must sign in again.
const RENEW_AHEAD_MS = 5 * 60_000;

export type Renewal =
  | { kind: "ok"; token: string }
  /** The session is over: the refresh token expired or was revoked. Sign in again. */
  | { kind: "signed_out" }
  /** Cognito could not be reached. The current token may still be good; try again later. */
  | { kind: "unreachable"; error: string };

/** A token good for at least a few more minutes, or why there is none. */
export async function renewToken(): Promise<Renewal> {
  try {
    let session = await fetchAuthSession();
    const expires = session.tokens?.idToken?.payload.exp;
    if (typeof expires === "number" && expires * 1000 - Date.now() < RENEW_AHEAD_MS) {
      session = await fetchAuthSession({ forceRefresh: true });
    }
    const token = session.tokens?.idToken?.toString();
    return token ? { kind: "ok", token } : { kind: "signed_out" };
  } catch (err) {
    // Amplify keeps the tokens on anything but a definitive refusal, which it
    // answers with no tokens rather than an error: so this is the network.
    return { kind: "unreachable", error: err instanceof Error ? err.message : String(err) };
  }
}

/** The signed-in user's email, for the audit line the API logs and the masthead. */
export async function currentViewer(): Promise<string | undefined> {
  try {
    const session = await fetchAuthSession();
    const email = session.tokens?.idToken?.payload.email;
    if (typeof email === "string" && email !== "") return email;
    const user = await getCurrentUser();
    return user.signInDetails?.loginId ?? user.username;
  } catch {
    return undefined;
  }
}

function stepFrom(output: SignInOutput): SignInStep {
  const next = output.nextStep;
  switch (next.signInStep) {
    case "DONE":
      return { kind: "done" };
    case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED":
      return { kind: "new_password" };
    case "CONTINUE_SIGN_IN_WITH_TOTP_SETUP":
      return {
        kind: "totp_setup",
        secret: next.totpSetupDetails.sharedSecret,
        uri: next.totpSetupDetails.getSetupUri("Betalingsservice files").toString(),
      };
    case "CONFIRM_SIGN_IN_WITH_TOTP_CODE":
      return { kind: "totp_code" };
    default:
      // Every other challenge (SMS, custom, select-factor) is one this pool is
      // not configured to set. Saying so beats a blank screen.
      throw new Error(`This account needs a sign-in step the inspector does not support (${next.signInStep}).`);
  }
}

export async function startSignIn(email: string, password: string): Promise<SignInStep> {
  // A stale session blocks a fresh sign-in with "already signed in"; clear it.
  if (await currentToken()) await amplifySignOut();
  return stepFrom(await amplifySignIn({ username: email, password }));
}

/** Answers whatever the pool asked for: a new password, or a TOTP code. */
export const answerChallenge = async (answer: string): Promise<SignInStep> =>
  stepFrom(await confirmSignIn({ challengeResponse: answer }));

export const signOut = (): Promise<void> => amplifySignOut();

/** Cognito's messages are usable as-is; its class names are not. */
export function describeAuthError(error: unknown): string {
  if (!(error instanceof Error)) return "Sign-in failed.";
  if (error.name === "NotAuthorizedException") return "That email and password do not match.";
  if (error.name === "UserNotFoundException") return "That email and password do not match.";
  if (error.name === "PasswordResetRequiredException") return "This account needs its password reset. Ask an administrator.";
  if (error.name === "TooManyRequestsException" || error.name === "LimitExceededException") {
    return "Too many attempts. Wait a minute and try again.";
  }
  if (error.name === "CodeMismatchException" || error.name === "EnableSoftwareTokenMFAException") {
    return "That code is not right. Codes change every 30 seconds — try the current one.";
  }
  if (error.name === "InvalidPasswordException") return error.message;
  return error.message || "Sign-in failed.";
}
